/**
 * Right-anchored drawer that walks the user from cart → confirmed booking.
 *
 * Three slides inside one container:
 *   1. Master picker (with "Any specialist" default)
 *   2. Date + time picker
 *   3. Client form (name + phone required, notes optional)
 *
 * Keeping all three in a single drawer lets us preserve scroll position,
 * animate transitions cheaply, and keep the cart visible behind the
 * scrim — alteg.io uses an in-page sidebar instead, but a drawer reads
 * better on small Armenian-language screens where labels run long.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  ANY_MASTER,
  useSalonBooking,
} from "./SalonBookingContext";
import {
  generateAnyMasterAvailability,
  generateAvailability,
  getMasterById,
  getMastersForServices,
} from "./mockSalonData";
import { MasterPicker } from "./MasterPicker";
import { DateTimePicker } from "./DateTimePicker";
import { formatDuration, formatPrice, pickLocalized } from "./salonTheme";

const STEP_MASTER = 0;
const STEP_TIME = 1;
const STEP_CLIENT = 2;
const TOTAL = 3;

function phoneIsValid(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length >= 7;
}

function nameIsValid(value) {
  return String(value || "").trim().length >= 2;
}

function StepDots({ palette, current }) {
  return (
    <div className="d-flex align-items-center gap-1" style={{ marginTop: 6 }}>
      {Array.from({ length: TOTAL }).map((_, i) => (
        <span
          key={i}
          aria-hidden="true"
          style={{
            width: i === current ? 22 : 8,
            height: 8,
            borderRadius: 999,
            background: i <= current ? palette.accent : palette.surfaceAlt,
            border: i <= current ? "none" : palette.border,
            transition: "all 0.2s ease",
          }}
        />
      ))}
    </div>
  );
}

export function CheckoutDrawer({
  palette,
  lang,
  isOpen,
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation();
  const {
    cart,
    masterId,
    setMaster,
    slot,
    setSlot,
    client,
    updateClient,
    totalDuration,
    totalPrice,
    hasOpenPriceFrom,
    bookingState,
  } = useSalonBooking();

  const [step, setStep] = useState(STEP_MASTER);
  const [touched, setTouched] = useState({ name: false, phone: false });

  useEffect(() => {
    if (isOpen) {
      setStep(STEP_MASTER);
      setTouched({ name: false, phone: false });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  const eligibleMasters = useMemo(
    () => getMastersForServices(cart.map((s) => s.id)),
    [cart],
  );

  const days = useMemo(() => {
    if (cart.length === 0) return [];
    if (masterId === ANY_MASTER) {
      return generateAnyMasterAvailability({
        serviceIds: cart.map((s) => s.id),
        totalDuration,
      });
    }
    return generateAvailability({ masterId, totalDuration }).map((day) => ({
      ...day,
      slots: day.slots.map((time) => ({ time, masterId })),
    }));
  }, [cart, masterId, totalDuration]);

  const goNext = useCallback(() => {
    setStep((s) => Math.min(s + 1, TOTAL - 1));
  }, []);
  const goBack = useCallback(() => {
    setStep((s) => Math.max(s - 1, 0));
  }, []);

  const handleSlotChange = useCallback(
    (next) => {
      setSlot(next);
      if (masterId === ANY_MASTER && next?.masterId) {
        setMaster(next.masterId);
      }
      goNext();
    },
    [setSlot, masterId, setMaster, goNext],
  );

  const masterRecord = useMemo(() => {
    if (slot?.masterId) return getMasterById(slot.masterId);
    if (masterId !== ANY_MASTER) return getMasterById(masterId);
    return null;
  }, [masterId, slot]);

  const nameValid = nameIsValid(client.name);
  const phoneValid = phoneIsValid(client.phone);
  const canConfirm =
    Boolean(slot) &&
    nameValid &&
    phoneValid &&
    bookingState !== "creating";

  if (!isOpen) return null;

  const stepTitles = [
    t("salonBooking.checkout.stepMasterTitle"),
    t("salonBooking.checkout.stepTimeTitle"),
    t("salonBooking.checkout.stepClientTitle"),
  ];

  const priceLabel = hasOpenPriceFrom
    ? `${t("salonBooking.service.from")} ${formatPrice(totalPrice, "AMD", lang)}`
    : formatPrice(totalPrice, "AMD", lang);

  const drawer = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="salon-checkout-title"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        zIndex: 1080,
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 560,
          background: palette.surface,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-20px 0 50px rgba(0,0,0,0.25)",
          maxHeight: "100vh",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 18px 12px",
            borderBottom: palette.border,
            background: palette.surface,
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: "0.72rem",
                  color: palette.textMuted,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  fontWeight: 600,
                }}
              >
                {t("salonBooking.checkout.stepLabel", {
                  current: step + 1,
                  total: TOTAL,
                })}
              </div>
              <h3
                id="salon-checkout-title"
                className="fw-700 mb-0"
                style={{
                  color: palette.text,
                  fontSize: "clamp(1.05rem, 2.5vw, 1.2rem)",
                  lineHeight: 1.25,
                  marginTop: 2,
                }}
              >
                {stepTitles[step]}
              </h3>
              <StepDots palette={palette} current={step} />
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("salonBooking.checkout.closeAria")}
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                border: palette.border,
                background: palette.surfaceAlt,
                color: palette.textMuted,
                cursor: "pointer",
                flexShrink: 0,
                marginLeft: 12,
              }}
            >
              <i className="las la-times" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "16px 18px",
            background: palette.pageBg || palette.surface,
          }}
        >
          {step === STEP_MASTER ? (
            <MasterPicker
              palette={palette}
              lang={lang}
              masters={eligibleMasters}
              selectedId={masterId}
              onSelect={(id) => {
                setMaster(id);
                goNext();
              }}
            />
          ) : null}

          {step === STEP_TIME ? (
            <DateTimePicker
              palette={palette}
              lang={lang}
              days={days}
              value={slot}
              onChange={handleSlotChange}
            />
          ) : null}

          {step === STEP_CLIENT ? (
            <div style={{ display: "grid", gap: 14 }}>
              {/* Summary card */}
              <div
                style={{
                  background: palette.surface,
                  border: palette.border,
                  borderRadius: 14,
                  padding: "12px 14px",
                }}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <span style={{ color: palette.textMuted, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    {t("salonBooking.checkout.summary")}
                  </span>
                  <button
                    type="button"
                    onClick={() => setStep(STEP_MASTER)}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: palette.accent,
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    {t("salonBooking.checkout.edit")}
                  </button>
                </div>
                <div className="fw-700" style={{ color: palette.text, fontSize: "1rem", marginTop: 4 }}>
                  {cart.map((s) => pickLocalized(s.name, lang)).join(", ")}
                </div>
                <div style={{ color: palette.textMuted, fontSize: "0.85rem", marginTop: 4 }}>
                  <i className="las la-clock me-1" aria-hidden="true" />
                  {formatDuration(totalDuration, lang)}
                  {" • "}
                  <span style={{ color: palette.text, fontWeight: 600 }}>{priceLabel}</span>
                </div>
                {slot ? (
                  <div style={{ color: palette.text, fontSize: "0.9rem", marginTop: 8 }}>
                    <i className="las la-calendar me-1" style={{ color: palette.accent }} aria-hidden="true" />
                    {slot.date} • <strong>{slot.time}</strong>
                  </div>
                ) : null}
                {masterRecord ? (
                  <div style={{ color: palette.text, fontSize: "0.9rem", marginTop: 4 }}>
                    <i className="las la-user me-1" style={{ color: palette.accent }} aria-hidden="true" />
                    {pickLocalized(masterRecord.name, lang)}
                  </div>
                ) : null}
              </div>

              <div>
                <label
                  className="d-block fw-600 mb-1"
                  style={{ color: palette.text, fontSize: "0.9rem" }}
                >
                  {t("salonBooking.client.nameLabel")} <span style={{ color: palette.danger }}>*</span>
                </label>
                <input
                  type="text"
                  value={client.name || ""}
                  onChange={(e) => updateClient({ name: e.target.value })}
                  onBlur={() => setTouched((p) => ({ ...p, name: true }))}
                  placeholder={t("salonBooking.client.namePlaceholder")}
                  autoComplete="name"
                  style={{
                    width: "100%",
                    background: palette.surface,
                    border:
                      touched.name && !nameValid
                        ? `1.5px solid ${palette.danger}`
                        : palette.border,
                    borderRadius: 12,
                    padding: "12px 14px",
                    color: palette.text,
                    fontSize: "1rem",
                    outline: "none",
                  }}
                />
                {touched.name && !nameValid ? (
                  <div style={{ marginTop: 6, fontSize: "0.78rem", color: palette.danger }}>
                    {t("salonBooking.client.nameInvalid")}
                  </div>
                ) : null}
              </div>

              <div>
                <label
                  className="d-block fw-600 mb-1"
                  style={{ color: palette.text, fontSize: "0.9rem" }}
                >
                  {t("salonBooking.client.phoneLabel")} <span style={{ color: palette.danger }}>*</span>
                </label>
                <input
                  type="tel"
                  value={client.phone || ""}
                  onChange={(e) => updateClient({ phone: e.target.value })}
                  onBlur={() => setTouched((p) => ({ ...p, phone: true }))}
                  placeholder="+374 99 12 34 56"
                  autoComplete="tel"
                  style={{
                    width: "100%",
                    background: palette.surface,
                    border:
                      touched.phone && !phoneValid
                        ? `1.5px solid ${palette.danger}`
                        : palette.border,
                    borderRadius: 12,
                    padding: "12px 14px",
                    color: palette.text,
                    fontSize: "1rem",
                    outline: "none",
                  }}
                />
                <div
                  style={{
                    marginTop: 6,
                    fontSize: "0.78rem",
                    color:
                      touched.phone && !phoneValid
                        ? palette.danger
                        : palette.textMuted,
                  }}
                >
                  {touched.phone && !phoneValid
                    ? t("salonBooking.client.phoneInvalid")
                    : t("salonBooking.client.phoneHint")}
                </div>
              </div>

              <div>
                <label
                  className="d-block fw-600 mb-1"
                  style={{ color: palette.text, fontSize: "0.9rem" }}
                >
                  {t("salonBooking.client.notesLabel")}
                </label>
                <textarea
                  value={client.notes || ""}
                  onChange={(e) => updateClient({ notes: e.target.value })}
                  placeholder={t("salonBooking.client.notesPlaceholder")}
                  rows={3}
                  style={{
                    width: "100%",
                    background: palette.surface,
                    border: palette.border,
                    borderRadius: 12,
                    padding: "12px 14px",
                    color: palette.text,
                    fontSize: "0.95rem",
                    outline: "none",
                    resize: "vertical",
                  }}
                />
              </div>

              <div
                style={{
                  fontSize: "0.78rem",
                  color: palette.textMuted,
                  lineHeight: 1.45,
                }}
              >
                <i
                  className="las la-shield-alt me-1"
                  style={{ color: palette.accent }}
                  aria-hidden="true"
                />
                {t("salonBooking.client.privacyHint")}
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "12px 18px 16px",
            borderTop: palette.border,
            background: palette.surface,
            display: "flex",
            gap: 8,
            alignItems: "center",
          }}
        >
          {step > 0 ? (
            <button
              type="button"
              onClick={goBack}
              className="fw-500"
              style={{
                background: "transparent",
                border: palette.border,
                color: palette.textMuted,
                borderRadius: 999,
                padding: "10px 18px",
                cursor: "pointer",
                fontSize: "0.9rem",
              }}
            >
              <i className="las la-arrow-left me-1" aria-hidden="true" />
              {t("salonBooking.checkout.back")}
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="fw-500"
              style={{
                background: "transparent",
                border: palette.border,
                color: palette.textMuted,
                borderRadius: 999,
                padding: "10px 18px",
                cursor: "pointer",
                fontSize: "0.9rem",
              }}
            >
              {t("salonBooking.checkout.cancel")}
            </button>
          )}

          <div style={{ flex: 1 }} />

          {step === STEP_MASTER ? (
            <button
              type="button"
              onClick={goNext}
              className="fw-700"
              style={{
                background: palette.brandGradient,
                color: "#fff",
                border: "none",
                borderRadius: 999,
                padding: "12px 22px",
                cursor: "pointer",
                fontSize: "0.95rem",
              }}
            >
              {t("salonBooking.checkout.next")}
              <i className="las la-arrow-right ms-1" aria-hidden="true" />
            </button>
          ) : null}

          {step === STEP_TIME ? (
            <button
              type="button"
              onClick={goNext}
              disabled={!slot}
              className="fw-700"
              style={{
                background: palette.brandGradient,
                color: "#fff",
                border: "none",
                borderRadius: 999,
                padding: "12px 22px",
                cursor: slot ? "pointer" : "not-allowed",
                fontSize: "0.95rem",
                opacity: slot ? 1 : 0.5,
              }}
            >
              {t("salonBooking.checkout.next")}
              <i className="las la-arrow-right ms-1" aria-hidden="true" />
            </button>
          ) : null}

          {step === STEP_CLIENT ? (
            <button
              type="button"
              onClick={() => {
                setTouched({ name: true, phone: true });
                if (canConfirm && typeof onConfirm === "function") onConfirm();
              }}
              disabled={!canConfirm}
              className="fw-700"
              style={{
                background: palette.brandGradient,
                color: "#fff",
                border: "none",
                borderRadius: 999,
                padding: "12px 22px",
                cursor: canConfirm ? "pointer" : "not-allowed",
                fontSize: "0.95rem",
                opacity: canConfirm ? 1 : 0.55,
                minWidth: 160,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              {bookingState === "creating" ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                  {t("salonBooking.checkout.confirming")}
                </>
              ) : (
                <>
                  <i className="las la-check" aria-hidden="true" />
                  {t("salonBooking.checkout.confirm")}
                </>
              )}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );

  return createPortal(drawer, document.body);
}
